import { createTheme } from '@mui/material/styles';
import createTypography from '@mui/material/styles/createTypography';
import createPalette from '@mui/material/styles/createPalette';
import type {} from '@mui/x-date-pickers/themeAugmentation';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true;
    tablet: true;
    laptop: true;
    miniDesktop: true;
    desktop: true;
  }
}

const palette = createPalette({
  primary: { main: '#0fb2a1', light: '#f3fbfa', dark: '#BD25B4' },
  secondary: { main: '#4e4Bdd' },
  grey: {
    100: '#fcfcfc',
    500: '#d8dbe0',
    700: '#d8dbe0',
    'A100': '#f7f7fb',
    'A700': '#828282',
  },
  text: {
    primary: '#000',
    secondary: '#fff',
    disabled: '#828282'
  },
  background: {
    default: '#fff',
  },
});

export const theme = createTheme({
  shape: {
    borderRadius: 3,
  },
  breakpoints: {
    values: {
      mobile: 320,
      tablet: 700,
      laptop: 1024,
      miniDesktop: 1320,
      desktop: 1450,
    }
  },
  palette: palette,
  typography: createTypography(palette, {
    fontFamily: `'Poppins', sans-serif`,
    button: {
      fontSize: 22,
      textTransform: 'none',
      color: palette.text?.primary,
      '@media screen and (max-width: 1024px)': {
        fontSize: 18,
      }
    },
    overline: {
      fontSize: 16,
      fontWeight: 700,
      textTransform: 'none',
      color: palette.text?.primary,
      '@media screen and (min-width: 1024px)': {
        fontSize: 18,
      },
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      color: palette.grey?.['A100'],
    },
    h1: {
      fontSize: 32,
      lineHeight: 1.2,
      fontWeight: 500,
      color: palette.text?.primary,
      '@media screen and (min-width: 1023px)': {
        fontSize: 58,
        lineHeight: 1.1,
      }
    },
    h2: {
      fontSize: 24,
      lineHeight: 1.2,
      fontWeight: 500,
      color: palette.text?.primary,
      '@media screen and (min-width: 1023px)': {
        fontSize: 38,
        lineHeight: 1.1,
      },
    },
    h3: {
      fontSize: 22,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      padding: '6px 0',
      color: palette.grey?.['A700'],
      '@media screen and (max-width: 1023px)': {
        fontSize: 16,
        fontWeight: 500,
      },
    },
    h4: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.4,
      color: palette.text?.primary,
      '@media screen and (min-width: 1023px)': {
        fontSize: 20,
        fontWeight: 400,
      },
    },
    h5: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.25,
      color: palette.text?.primary,
      '@media screen and (min-width: 1023px)': {
        fontSize: 28,
      },
    },
    h6: {
      fontSize: 16,
      lineHeight: 1.33,
      fontWeight: 600,
      color: palette.text?.primary,
      '@media screen and (min-width: 1023px)': {
        fontSize: 18,
      },
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      color: palette.grey?.['A700'],
      '@media screen and (max-width: 1023px)': {
        fontSize: 12,
      },
    },
    body2: {
      fontSize: 16,
      lineHeight: 1.2,
      fontWeight: 400,
      '@media screen and (max-width: 1023px)': {
        fontSize: 12,
      },
    },
    subtitle1: {
      fontSize: 18,
      fontWeight: 600,
      color: palette.secondary.main,
      '@media screen and (max-width: 1023px)': {
        fontSize: 16
      }
    },
    subtitle2: {
      fontSize: 14,
      lineHeight: 1.4,
      color: palette.secondary.main,
      '@media screen and (max-width: 1023px)': {
        fontSize: 12,
      }
    },
  }),
  components: {
    MuiIcon: {
      defaultProps: {
        baseClassName: 'material-icons-outlined',
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          color: '#fff',
        },
        containedPrimary: {
          '&:hover': {
            color: '#fff',
            background: palette.primary.main
          }
        },
        sizeMedium: {
          fontSize: 16,
          fontWeight: 300,
          padding: '6px 28px',
          borderRadius: 6
        },
      },
      defaultProps: {
        sx: {
          '&:hover:disabled': {
            backgroundColor: palette.grey.A200
          },
          '@media screen and (max-width: 1023px)': {
            fontSize: 14,
            padding: 1.5,
          }
        }
      }
    },
    MuiSnackbar: {
      defaultProps: {
        sx: {
          '& .MuiAlert-filledSuccess': {
            backgroundColor: palette.primary.main
          }
        }
      }
    },
    MuiDatePicker: {
      defaultProps: {},
      styleOverrides: {
        '& .MuiPickersDay-Root': {
          '& .Mui-selected': {
            backgroundColor: palette.primary.main
          }
        },
        root: {
          color: palette.primary.main,
          '& .MuiPickersDay-Root': {
            '& .Mui-selected': {
              backgroundColor: palette.primary.main
            }
          },

        },
      },
    },
    MuiTextField: {
      variants: [
        {
          props: { variant: 'standard', disabled: true },
          style: {
            '& .Mui-disabled': {
              '& svg': {
                display: 'none'
              }
            }
          },
        },
        {
          props: { variant : 'outlined'},
          style: {
            '& fieldset': {
              borderRadius: 10
            }
          }
        }
      ]
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: palette.grey.A700,
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.background.default,
          color: palette.primary.main
        }
      }

    }
  }
});
